//* 상품 간단 구매 모달

import { FC } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { Dialog } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { useTestShopItemListForBugoHook } from '@shared/api/shopItem/shopItem.hook';
import { testShopItemListForBugoStateSelector } from '@shared/api/shopItem/shotItem.selector';
import { Button } from '@shared/components/Button';
import { useModalHook } from '@shared/hooks/useModalHook';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { useId } from 'react-id-generator';
import { Link } from 'react-router-dom';

import { EasyBuyShopItemCard } from './EasyBuyShopItemCard';

interface Props {
  bugoId: string;
}

export const FlowerEasyBuyModalButton: FC<Props> = function FlowerEasyBuyModalButton({
  bugoId,
}: Props) {
  const [id] = useId(1, 'flower-easy-buy-modal-button');
  const openId = `open-${id}`;
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const customTheme = useRecoilValue(customThemeAtom);
  useModalHook(openId);
  useTestShopItemListForBugoHook(bugoId);
  const { status, data } = useRecoilValue(testShopItemListForBugoStateSelector);
  const shopItems = status === 'success' ? data?.slice(0, 3) ?? [] : [];
  const loading = status === 'idle' || status === 'loading';

  const close = () => {
    setOpen(false);
    history.back();
  };

  return (
    <>
      <Button
        className="h-10 px-3.5 text-white shadow-none theme-bg-main"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        간편구매
      </Button>
      <Dialog open={open} onClose={close} className={`${customTheme} fixed inset-0 z-40`}>
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-6">
            <Dialog.Panel className="w-full max-w-[440px] rounded-2xl bg-white px-4 pb-2 pt-4 shadow-xl sm:px-6 sm:pt-5">
              <div className="flex items-center justify-between gap-2">
                <Dialog.Title className="text-18 font-bold text-gray-800">
                  근조화환 간편구매
                </Dialog.Title>
                <button
                  type="button"
                  aria-label="간편구매 닫기"
                  onClick={close}
                  className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <Dialog.Description className="mb-4 text-13 leading-5 text-gray-500">
                주소 입력 없이 간편하게 보내세요.
              </Dialog.Description>
              {loading ? (
                <p role="status" className="py-10 text-center text-sm text-gray-500">
                  상품을 불러오고 있습니다.
                </p>
              ) : shopItems.length > 0 ? (
                <div className="divide-y divide-gray-100 border-t border-gray-100">
                  {shopItems.map((shopItem) => (
                    <EasyBuyShopItemCard
                      shopItem={shopItem}
                      bugoId={bugoId}
                      key={shopItem._id}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-4 py-6 text-center">
                  <p role="status" className="text-sm text-gray-600">
                    {status === 'error'
                      ? '상품을 불러오지 못했습니다.'
                      : '현재 간편구매로 안내할 상품이 없습니다.'}
                  </p>
                  <Link
                    to={`/flower-store/?bugoId=${bugoId}`}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700"
                  >
                    화환 전체 보기
                  </Link>
                </div>
              )}
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </>
  );
};
